import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { LOAD_ASSET, UNLOAD_ASSET } from 'store/actions';
import { selectIsAssetLoaded } from 'store/assets/selectors';

import { AssetExplorer } from './AssetExplorer';
import { AssetStack } from './AssetStack';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.OFFLINE],
  Screens.OFFLINE_ASSET
>;

export const AssetScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const { id } = route.params;

  // Load asset if it isn't already loaded. It should be loaded though.
  const isAssetLoaded = useSelector((state) => selectIsAssetLoaded(state, id));

  useEffect(() => {
    if (!isAssetLoaded) {
      dispatch(LOAD_ASSET.request(id));
    }
  }, [isAssetLoaded, dispatch, id, screenSize, navigation]);

  // Unload asset when closed
  useEffect(() => {
    return () => {
      dispatch(UNLOAD_ASSET(id));
    };
  }, [dispatch, id]);

  // useMemo here to gate the rest of the asset page from
  // re-rendering when anything related to navigation changes
  return useMemo(
    () =>
      !isAssetLoaded ? null : screenSize === 'large' ? (
        <View style={styles.container}>
          <View style={styles.explorerContainer}>
            <AssetExplorer
              navigation={
                navigation as unknown as NativeStackScreenProps<
                  NavigationStacks[Stacks.ASSET],
                  Screens.ASSET_EXPLORER
                >['navigation']
              }
              route={{
                key: Screens.ASSET_EXPLORER,
                name: Screens.ASSET_EXPLORER,
                params: { assetId: id },
              }}
            />
          </View>
          <View style={styles.navigatorContainer}>
            <AssetStack assetId={id} externalExplorer />
          </View>
        </View>
      ) : (
        <AssetStack assetId={id} />
      ),
    [isAssetLoaded, screenSize, navigation, id],
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
  },
  explorerContainer: {
    borderRightColor: '#DDDDDD',
    borderRightWidth: 1,
    flex: 1,
  },
  navigatorContainer: {
    flex: 1,
    overflow: 'hidden',
  },
});
